/**
 * Privacy Policy — English translation.
 *
 * IMPORTANT: This is an unofficial translation for convenience only.
 * The German version (DatenschutzContentDe.tsx) is legally binding under
 * German law and the GDPR. When updating, keep both files in sync.
 *
 * Last updated: 2026-05-24 | Version: 2.4
 */

export function DatenschutzContentEn() {
  return (
    <div className="space-y-12">
      {/* Scope */}
      <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-emerald-900 mb-3">
          Scope of this Privacy Policy
        </h2>
        <p className="text-slate-700 leading-relaxed">
          This Privacy Policy applies to <strong>all Equna services</strong>:
        </p>
        <ul className="list-disc list-inside text-slate-700 leading-relaxed mt-2 space-y-1">
          <li>Our website <strong>equna.eu</strong> and all subpages</li>
          <li>The Equna web app at <strong>app.equna.eu</strong></li>
          <li>The Equna mobile app for <strong>Android</strong> (Google Play Store) and <strong>iOS</strong> (Apple App Store)</li>
        </ul>
        <p className="text-slate-700 leading-relaxed mt-3">
          Wherever this policy refers to &ldquo;Equna&rdquo;, &ldquo;the app&rdquo;, or &ldquo;the service&rdquo;, all three platforms are meant. Where certain sections apply only to the website or the app, this is indicated explicitly.
        </p>
      </section>

      {/* Section 1: Overview */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          1. Privacy at a Glance
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">General Information</h3>
            <p className="text-slate-600 leading-relaxed">
              The following notes provide a simple overview of what happens to your personal data when you visit our website or use the Equna app. Personal data is any data that can be used to identify you personally.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Data Collection at Equna</h3>
            <div className="space-y-4">
              <p className="text-slate-600 leading-relaxed">
                <strong className="block text-slate-800 mb-1">Who is responsible for data collection?</strong>
                Data processing is carried out by the website operator. You can find contact details in section 3.3 and in the imprint.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong className="block text-slate-800 mb-1">How do we collect your data?</strong>
                We collect personal data only if you voluntarily provide it (e.g. registration, entering expenses, email) or if technically necessary data is automatically generated when you visit the page (server logs).
              </p>
              <p className="text-slate-600 leading-relaxed">
                Technically necessary data (e.g. IP address, browser type, operating system, time of access) is automatically recorded by our hosting provider in so-called server log files when you visit the website. This serves the secure and stable provision of the service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Hosting */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          2. Hosting
        </h2>
        <div className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-lg border border-slate-100">
            <p className="text-slate-800 mb-2">
              Our website and app are hosted by <strong>IONOS SE</strong>.
            </p>
            <address className="not-italic text-slate-600">
              Elgendorfer Str. 57<br />
              56410 Montabaur<br />
              Germany
            </address>
          </div>
          <p className="text-slate-600 leading-relaxed">
            When you visit our website, IONOS records various log files including your IP address. The use of IONOS is based on Art. 6(1)(f) GDPR. We have a legitimate interest in the most reliable presentation of our website.
          </p>
          <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r">
            <strong>Data processing agreement:</strong><br />
            We have concluded a data processing agreement (DPA) with IONOS in accordance with Art. 28 GDPR.
          </p>
        </div>
      </section>

      {/* Section 3: General */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          3. General Information and Mandatory Information
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.1 Data Protection</h3>
            <p className="text-slate-600 leading-relaxed">
              We take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with statutory data-protection regulations and this privacy policy.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.2 External Services and Fonts</h3>
            <p className="text-slate-600 leading-relaxed">
              We embed fonts and images locally from our own server. No connection is established to third-party servers (e.g. Google or other external providers). Your IP address is not transmitted to such providers.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.3 Controller</h3>
            <div className="space-y-4">
              <p className="text-slate-600 leading-relaxed">The controller for data processing is:</p>
              <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200 inline-block pr-12">
                <strong className="text-slate-900">Patrizio Veglia</strong><br />
                Herbststr. 9<br />
                13409 Berlin<br />
                Germany
              </address>
              <p className="text-slate-600">
                Email:{" "}
                <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  contact@equna.eu
                </a>
              </p>
              <p className="text-slate-500 text-sm italic">
                The controller is the natural or legal person who, alone or jointly with others, determines the purposes and means of the processing of personal data.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.4 Data Protection Officer</h3>
            <p className="text-slate-600 leading-relaxed">
              No data protection officer has been appointed, as the legal prerequisites under Art. 37(1) GDPR are not met (no core activity involving large-scale processing of special categories of data, and no continuous employment of 20 or more persons engaged in automated processing). Please direct data protection enquiries to the controller at <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">contact@equna.eu</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: Processing of your data and security */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          4. Processing of Your Data and Security
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.1 Account Data at Registration</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Using Equna requires a user account. The following data is processed:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Data:</strong> email address, display name of your choice, password (stored only as a BCrypt hash)</li>
              <li><strong>Purpose:</strong> account creation, login, assignment of expenses to your account, communication (e.g. password reset, invitations)</li>
              <li><strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of contract)</li>
              <li><strong>Retention:</strong> until full deletion of your account; thereafter possibly up to 30 days in rotating backups</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Mandatory provision:</strong> Providing a valid email address and password is a mandatory prerequisite for concluding the contract. Without this data, account creation is technically impossible.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.2 Expense, Budget and Group Data</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              The core purpose of Equna is sharing and managing joint expenses within a group (e.g. couples, co-parents):
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Data:</strong> expense amounts, categories, dates, descriptions (free text), group and partner assignments, distribution keys, budgets, monthly settlement confirmations</li>
              <li><strong>Purpose:</strong> splitting expenses, budget control, monthly settlement and balancing between group members</li>
              <li><strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of contract — core functionality)</li>
              <li><strong>Visibility:</strong> exclusively within your group (you and your partner(s))</li>
              <li><strong>Retention:</strong> until account deletion or manual deletion of the expense by you</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Note on free-text fields:</strong> Please do not enter particularly sensitive data about yourself or third parties (e.g. health information, payment details, contents of private communication) in free-text fields (expense description, comments). Equna stores free text unchanged.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.3 Server Logs and Access Statistics</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              On every access, technically necessary log data is automatically collected by our hosting provider (IONOS SE, Germany):
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Data:</strong> IP address, timestamp, URL accessed, HTTP status code, browser/device identifier (user agent), referrer</li>
              <li><strong>Purpose:</strong> stable and secure operation of the service, error analysis, abuse prevention</li>
              <li><strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in secure operation)</li>
              <li><strong>Retention:</strong> usually 14 days, then automatic deletion. In case of attack patterns, up to 30 days for forensic analysis.</li>
              <li><strong>No profiling:</strong> logs are not linked to your user identity for tracking purposes.</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.4 Password Security</h3>
            <p className="text-slate-600 leading-relaxed">
              Your passwords are <strong>never stored in plain text</strong>. We use the industry-standard <strong>BCrypt</strong> algorithm with automatic salt for encryption.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.5 JWT Tokens and Refresh Tokens</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              For authentication we use <strong>JSON Web Tokens (JWT)</strong> and <strong>refresh tokens</strong>:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>JWT token:</strong> Valid for 60 minutes, stored in browser memory</li>
              <li><strong>Refresh token:</strong> Valid for 90 days, stored as an <strong>HttpOnly cookie</strong> (XSS protection)</li>
              <li><strong>Secure flag:</strong> Cookies are only transmitted over HTTPS</li>
              <li><strong>SameSite flag:</strong> Protection against CSRF attacks</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (technical necessity for login functionality)
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.6 Encryption</h3>
            <p className="text-slate-600 leading-relaxed">
              All data transmission between your device and our servers takes place via <strong>HTTPS (TLS 1.3)</strong>.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.7 Rate Limiting and Brute-Force Protection</h3>
            <p className="text-slate-600 leading-relaxed">
              To protect against unauthorised access, we limit the number of login attempts. After five failed login attempts, the account is temporarily locked. In addition, suspicious access patterns (e.g. many requests from the same IP address within a short period) are automatically blocked.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in account security and protection against abuse)
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Data transfer to third parties */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          5. Data Transfer to Third Parties
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.1 Hosting</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Our website and app are hosted by the following provider:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong><br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <p className="text-slate-600 leading-relaxed mt-3">
              The hosting provider processes the following data on our behalf: server logs (IP addresses, access times) and database content (encrypted).
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Legal basis:</strong> Art. 28 GDPR (data processing on behalf)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Data processing agreement (DPA):</strong> In place (pursuant to Art. 28(3) GDPR)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.2 Cloud Storage (Receipt Photos) &amp; OCR</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              When you upload photos of receipts in the app, they are stored in our cloud storage:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong> (S3-compatible object storage)<br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <div className="mt-3 space-y-2">
              <p className="text-slate-600 leading-relaxed">
                <strong>Stored data:</strong> uploaded receipt photos (JPEG/PNG) and a link to the corresponding expense.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Retention:</strong> Free Tier: 60 days after upload. Premium Tier: 365 days after upload.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of contract)
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Deletion:</strong> Receipt photos are automatically deleted after the retention period expires. You can delete photos manually in the app at any time.
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>OCR on our own server:</strong> Receipt photos are optionally processed by our self-hosted OCR engine (PaddleOCR) to suggest amounts and categories. The images <strong>never leave our server in Germany</strong> — <strong>no external AI or cloud services</strong> such as Google Vision, AWS Textract or similar are used.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.3 Email Delivery</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              For sending emails (e.g. password reset) we use:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong> (SMTP server)<br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of contract)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              5.4 Push Notifications (Firebase Cloud Messaging — Privacy by Design)
            </h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              For real-time notifications on Android/iOS we use:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Google Firebase (Firebase Cloud Messaging / FCM)</strong><br />
              Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland
            </address>
            <div className="mt-3">
              <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mb-3">
                <strong>Privacy-by-design approach:</strong> Only a <strong>technical trigger</strong> (notification ID + event type) is transmitted to Google. <strong>No personal data</strong> (names, amounts, categories) is sent to FCM. The actual notification content is loaded directly and encrypted from our server in Germany.
              </p>
              <div className="space-y-2">
                <p className="text-slate-600 leading-relaxed">
                  <strong>Stored data:</strong> FCM token (for push delivery), locally generated device ID (UUID, stored exclusively on your device in LocalStorage), platform (android/ios), device model (optional, for error analysis).
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Legal basis:</strong> Art. 6(1)(a) GDPR (consent via push activation)
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Retention:</strong> Until logout or uninstallation of the app (inactive tokens deleted after 90 days).
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>International data transfer:</strong> Our contractual partner is Google Ireland Limited (EU). The parent company Google LLC (USA) may transfer data to the United States in the course of providing the service. Legal basis: EU-US Data Privacy Framework (Google is actively certified, see <a href="https://www.dataprivacyframework.gov/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">dataprivacyframework.gov</a>) and EU Standard Contractual Clauses (Art. 46 GDPR) as additional safeguard.
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed border-l-4 border-amber-500 pl-4 bg-amber-50/50 py-2 rounded-r mt-3">
              <strong>Current status:</strong> Push notifications are currently disabled. They will only be enabled once the feature is released. You will be notified in the app upon activation and may object to the processing at any time by disabling them in system settings.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.5 System Share Function (Invite Links)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              When you send an invitation to your partner from within the app, we use the <strong>native share function</strong> of Android or iOS. The app hands the invitation link over to the operating system, which then presents you with a selection menu for messengers, email apps, etc.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Data transmitted:</strong> the invite link (containing a random invitation code, no names or email addresses)</li>
              <li><strong>Recipient:</strong> the app of your choice (e.g. WhatsApp, Signal, email) — this third-party provider is <strong>not controlled by Equna</strong></li>
              <li><strong>Our role:</strong> we receive no information about which app or person you forward the link to</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal basis:</strong> Art. 6(1)(a) GDPR (consent by deliberately triggering the share function)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Important:</strong> The privacy policy of the third-party app you choose (WhatsApp, email provider, etc.) applies. Equna has no influence on that.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.6 Have I Been Pwned (Password Security Check)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              During registration and password changes, we check whether the chosen password has appeared in known data breaches. For this we use the <strong>Have I Been Pwned</strong> service (operated by Troy Hunt, Australia/USA).
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Data transmitted:</strong> only the first 5 characters of a SHA-1 hash of the password (k-anonymity)</li>
              <li><strong>Your password is NEVER transmitted</strong> — neither in plain text nor as a complete hash</li>
              <li><strong>No storage</strong> of the transmitted hash prefixes either by us or by Have I Been Pwned</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in account security and protection against unauthorised access)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              ✓ Privacy-friendly through k-anonymity (hash-prefix method)
            </p>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>International data transfer:</strong> The transmitted 5-character hash prefix is, according to the prevailing view, not personal data within the meaning of Art. 4(1) GDPR, as approximately 500 possible passwords map to it (k-anonymity) and no identification is possible. A transfer to a third country within the meaning of Art. 44 GDPR therefore does not take place.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.7 Reactions and Comments in Groups</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Within a group, users can leave <strong>emoji reactions</strong> and <strong>comments</strong> on expenses. These are stored on our servers and visible to all members of the respective group.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Stored data:</strong> emoji/reaction or comment text, creator ID, timestamp, reference to the expense</li>
              <li><strong>Visibility:</strong> only within the group — never public, never to third parties</li>
              <li><strong>Deletion:</strong> together with the associated expense or manually by the creator</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal basis:</strong> Art. 6(1)(b) GDPR (performance of contract — communication between group members is a core part of the service)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.8 Cloudflare (Content Delivery Network for the Website)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Delivery of static content for our website <strong>equna.eu</strong> uses the CDN network of Cloudflare, Inc., included in the IONOS web-hosting package.
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Cloudflare, Inc.</strong><br />
              101 Townsend St., San Francisco, CA 94107, USA
            </address>
            <div className="mt-3 space-y-2">
              <p className="text-slate-600 leading-relaxed">
                <strong>Processed data:</strong> IP address, request metadata (URL, timestamp, user agent), delivered static assets (CSS, images, fonts). For German visitors the Cloudflare edge server in Frankfurt is preferred.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Purpose:</strong> Accelerated delivery of the website, protection against DDoS attacks, technical stability.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in a fast and stable website)
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>International data transfer:</strong> Cloudflare is headquartered in the USA. Processing for German visitors is regularly carried out on the EU edge server in Frankfurt; a transfer to the USA cannot be ruled out entirely. Legal basis: EU-US Data Privacy Framework (Cloudflare is actively certified) and EU Standard Contractual Clauses (Art. 46 GDPR).
              </p>
              <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r">
                <strong>Important:</strong> Cloudflare is used exclusively for the static Landing Page (equna.eu) — <strong>not</strong> for the application itself (app.equna.eu) and <strong>not</strong> for the API. Your content data (expenses, budgets, receipts) <strong>never passes through Cloudflare</strong>.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.9 Summary: International Data Transfers</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Your content data (expenses, budgets, receipts, account information) is processed exclusively on German servers in Frankfurt. In the following exceptional cases, transfers to third countries occur:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-2">
              <li>
                <strong>Cloudflare (USA)</strong> — CDN for static Landing Page content. Basis: EU-US DPF + SCCs. No content data.
              </li>
              <li>
                <strong>Firebase/Google LLC (USA)</strong> — <em>in future</em>, upon activation of push notifications. Only technical triggers, no content. Basis: EU-US DPF + SCCs.
              </li>
              <li>
                <strong>Have I Been Pwned (Cloudflare infrastructure, provider in Australia)</strong> — password security check via hash prefix. Due to k-anonymity, no personal data transfer within the meaning of Art. 44 GDPR.
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>All your content data</strong> — all expenses, budgets, receipts, account information, group and comment data — is stored and processed <strong>exclusively on German IONOS servers</strong>. This data does not leave Germany.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.10 No Transfer to Third Parties Without Consent</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              We do <strong>not</strong> pass on your personal data to third parties, except:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li>You have explicitly consented (Art. 6(1)(a) GDPR)</li>
              <li>There is a legal obligation (Art. 6(1)(c) GDPR)</li>
              <li>It is necessary for the performance of the contract (Art. 6(1)(b) GDPR)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 6: Cookies and local storage */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          6. Cookies and Local Storage
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.1 What are cookies?</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Cookies are small text files stored on your device. We use <strong>exclusively technically necessary cookies and local storage</strong>. All serve the operation of the website and app and do not require consent:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>RefreshToken cookie:</strong> login functionality (valid 90 days)</li>
              <li><strong>Session cookie:</strong> session management (until the browser is closed)</li>
              <li><strong>Preference storage:</strong> your UI settings (theme, language) in your browser&rsquo;s local storage</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Legal bases:</strong> § 25(2)(2) TDDDG (access to your device is strictly necessary to provide the requested service) and Art. 6(1)(b) or (f) GDPR for the subsequent processing (performance of contract / legitimate interest in secure operation).
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>Note:</strong> We do <strong>not use analytics, tracking or advertising cookies</strong>. No cookie consent banner is used because no consent-requiring services are employed.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.2 Local Storage (LocalStorage, IndexedDB)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              We store the following data <strong>locally on your device</strong> (no server access):
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Personal expenses:</strong> stored only on your device — in the browser via IndexedDB/LocalStorage, on mobile devices in an encrypted SQLite database (<strong>SQLCipher with AES-256</strong>)</li>
              <li><strong>Personal budgets:</strong> stored only on your device (same encryption)</li>
              <li><strong>UI preferences:</strong> theme, language, filter settings</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              This data <strong>never leaves your device</strong> and is not transmitted to our servers. The encryption key is stored securely in the Keychain (iOS) or EncryptedSharedPreferences (Android).
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong>OS backups (iCloud / Google Backup):</strong> If you have enabled the built-in backup function on your mobile device, app data — like that of other apps — may be included in the respective cloud backup depending on OS settings (Apple iCloud or Google One / Google Drive). These backups are subject to the data protection responsibility of Apple or Google. Our encrypted mobile database (SQLCipher with AES-256) remains encrypted in the backup. You can control this behaviour at any time in your device&rsquo;s system settings.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.3 App Permissions (Android &amp; iOS)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              The Equna app requests the following permissions. All permissions are <strong>optional</strong> and only requested at runtime — they are never granted silently in the background.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Camera:</strong> photographing receipts directly in the app (optional, only when adding a receipt)</li>
              <li><strong>Photo library (read):</strong> selecting existing photos as receipts</li>
              <li><strong>Photo library (write, iOS):</strong> saving a newly captured receipt photo to your gallery</li>
              <li><strong>Push notifications:</strong> receiving real-time notifications (new expense from partner, budget warning, monthly settlement)</li>
              <li><strong>Internet:</strong> connecting to the Equna server (technical prerequisite, no runtime prompt)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>Not requested:</strong> location, contacts, microphone, calendar, phone, SMS, Bluetooth, motion data, biometric data.
            </p>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Revocation:</strong> All permissions can be revoked at any time in the system settings (Android: Settings → Apps → Equna; iOS: Settings → Equna). The app also works without camera or push permission (with limited functionality).
            </p>
          </div>
        </div>
      </section>

      {/* Section 7: Your rights */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          7. Your Rights as a Data Subject
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.1 Right of Access (Art. 15 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">
              You have the right to obtain information about your stored personal data at any time.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In the app:</strong> Profile → &ldquo;Data Access &amp; Export (GDPR-compliant)&rdquo;. All users — Free as well as Premium — can export their full data as a JSON file at any time. Premium users additionally have an inline display in the profile as a convenience feature. The substantive scope of the data provided under Art. 15 GDPR is identical in both tiers.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.2 Right to Rectification (Art. 16 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">You may correct your data at any time.</p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In the app:</strong> Profile → &ldquo;Personal Information&rdquo; → edit data
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.3 Right to Erasure (Art. 17 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">You have the right to request the deletion of your personal data.</p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In the app:</strong> Profile → &ldquo;Delete account&rdquo; → security confirmation → account will be fully deleted
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Note on backups:</strong> After account deletion, individual data may remain for a short period (max. 30 days) in rotating backups until those backups are overwritten. Recovery after deletion is impossible.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.4 Right to Data Portability (Art. 20 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">
              You have the right to receive your data in a structured, commonly used, and machine-readable format. This right is available to <strong>all users — Free as well as Premium — to its full extent</strong>.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In the app:</strong> Profile → &ldquo;Data Access &amp; Export&rdquo; → &ldquo;Export as JSON&rdquo;
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Format:</strong> JSON (machine-readable, importable into other services)
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.5 Right to Object (Art. 21 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">
              You have the right to object to the processing of your personal data.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Contact:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.6 Right to Restriction of Processing (Art. 18 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">You may request the restriction of the processing of your data.</p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Contact:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.7 Right to Withdraw Consent</h3>
            <p className="text-slate-600 leading-relaxed">
              If you have given consent to data processing, you can withdraw it at any time. The lawfulness of processing carried out before the withdrawal is not affected.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1 mt-2">
              <li><strong>Push notifications:</strong> Disable directly in your system settings (Android: Settings → Apps → Equna; iOS: Settings → Equna).</li>
              <li><strong>Other consents:</strong> Via email to <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">contact@equna.eu</a>.</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.8 No Automated Decision-Making (Art. 22 GDPR)</h3>
            <p className="text-slate-600 leading-relaxed">
              There is <strong>no automated decision-making</strong> in the sense of Art. 22 GDPR. In particular, <strong>no profiles</strong> are created that would have legal effect or similarly significantly affect you.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              All decisions (e.g. category suggestions from OCR receipt recognition) are merely suggestions and only take effect once you manually confirm them.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8: Minors */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          8. Minors and Data About Children
        </h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">8.1 Age Restriction</h3>
            <p className="text-slate-600 leading-relaxed">
              Our app is <strong>not directed at persons under 16</strong>. If you become aware that a minor has created an account without parental consent, please contact us immediately.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">8.2 Data About Children (Co-parenting Context)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Equna is aimed at adults for the management of shared expenses — including in a co-parenting context. Users may document expenses concerning their children.
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-3 rounded-r mb-3">
              <strong>Privacy by design (Art. 25 GDPR):</strong> The app does <strong>not</strong> provide for the structured collection of sensitive child data. There are no input fields for date of birth, health data, addresses, or other sensitive information about children.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1 mb-3">
              <li><strong>Free Tier:</strong> Expenses can be assigned to the generic category &ldquo;Child&rdquo; — no names or other child data are stored in a structured way.</li>
              <li><strong>Premium Tier:</strong> Users may optionally create child profiles with <strong>first names</strong> to assign expenses. Further fields (age, date of birth, health, etc.) are not technically supported.</li>
              <li><strong>Free-text fields</strong> (e.g. description of an expense): The user is responsible for the information they enter. We explicitly advise against entering sensitive data about children (e.g. health status, school problems) in free-text fields.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              <strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest of the parent in documenting child-related expenses, particularly for co-parenting settlements).
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Retention:</strong> Same as the associated expense or until account deletion. Child profiles (Premium) can be deleted manually at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Section 9: Right to complain */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          9. Right to Complain to a Supervisory Authority
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            You have the right to lodge a complaint with a data-protection supervisory authority regarding the processing of your personal data.
          </p>
          <div>
            <p className="text-slate-800 font-medium mb-2">Responsible supervisory authority:</p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong><br />
              Alt-Moabit 59-61<br />
              10555 Berlin, Germany<br />
              <span className="text-slate-500 text-sm">(Entrance: Alt-Moabit 60)</span><br />
              Phone: +49 30 13889-0<br />
              Fax: +49 30 2155050<br />
              E-Mail: <a href="mailto:mailbox@datenschutz-berlin.de" className="text-emerald-600 hover:text-emerald-700 font-medium">mailbox@datenschutz-berlin.de</a><br />
              <a href="https://www.datenschutz-berlin.de/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">
                www.datenschutz-berlin.de
              </a>
            </address>
          </div>
          <p className="text-slate-600 leading-relaxed">
            <strong>List of all authorities:</strong>{" "}
            <a href="https://www.datenschutzkonferenz-online.de/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">
              www.datenschutzkonferenz-online.de
            </a>
          </p>
        </div>
      </section>

      {/* Section 10: Changes */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          10. Changes to this Privacy Policy
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            We reserve the right to update this privacy policy to adapt it to changed legal situations or changes to our services.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <strong>Notification:</strong> For material changes, we will inform you by email or in-app notification.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <strong>Recommendation:</strong> Check this page regularly for updates.
          </p>
        </div>
      </section>

      {/* Section 11: Contact */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          11. Contact for Data Protection Questions
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            If you have questions about data protection, you can contact us at any time:
          </p>
          <div className="space-y-2">
            <p className="text-slate-600 leading-relaxed">
              <strong>Email:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>Mail:</strong> Patrizio Veglia, Herbststr. 9, 13409 Berlin, Germany
            </p>
          </div>
        </div>
      </section>

      {/* Version */}
      <div className="pt-6 border-t border-slate-200 text-slate-500 text-sm">
        <p><strong>Last updated:</strong> 24 May 2026</p>
        <p><strong>Version:</strong> 2.4 — Updated contact details of the responsible supervisory authority (BlnBDI: new address Alt-Moabit 59-61, 10555 Berlin, plus phone/fax/e-mail).</p>
        <p className="mt-2"><strong>Version 2.3</strong> (21 April 2026) — Additions. Transparency note on the data protection officer (3.4: not appointed, as the prerequisites under Art. 37 GDPR are not met). Note on OS-level backup functions iCloud / Google Backup (6.2) for mobile devices.</p>
        <p className="mt-2"><strong>Version 2.2</strong> (21 April 2026) — Cookies (6.1) under § 25 TDDDG; right to withdraw consent (7.7) adjusted to actual implementation; data-subject rights 7.1/7.4 clarified as Premium-independent.</p>
        <p className="mt-2"><strong>Version 2.1</strong> (21 April 2026) — Structural overhaul with dedicated sections on account data, expenses/budgets/group data, server logs, Cloudflare CDN, and international transfer summary.</p>
        <p className="mt-2"><strong>Version 2.0</strong> (21 April 2026) — Canonical single-source-of-truth version for website &amp; app with SQLCipher, PaddleOCR, system share and local device ID.</p>
      </div>
    </div>
  );
}
